import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/20x20/edit.svg';
import { ReactComponent as ResetIcon } from '@elementary/common-ui/assets/20x20/reset.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import {
  Flex,
  Menu,
  IconButton,
  Loading,
} from '@elementary/common-ui/components';
import { useAuth } from '@frontegg/react';
import { useMutation } from '@tanstack/react-query';
import * as api from 'shared/services/settingsApi';

const MenuWrapper = styled(Flex)`
  justify-content: flex-end;
`;

type Props = {
  id: string;
  email: string;
  verified?: boolean;
  setUpdateUserId: React.Dispatch<React.SetStateAction<any>>;
  setUpdateUserModalOpen: React.Dispatch<React.SetStateAction<any>>;
  setDeletedUserId: React.Dispatch<React.SetStateAction<any>>;
  setDeleteUserModalOpen: React.Dispatch<React.SetStateAction<any>>;
  setDeleteUserName: React.Dispatch<React.SetStateAction<any>>;
};

const UserMenu = ({
  id,
  email,
  verified,
  setUpdateUserId,
  setUpdateUserModalOpen,
  setDeletedUserId,
  setDeleteUserModalOpen,
  setDeleteUserName,
}: Props) => {
  const { user } = useAuth();

  const { mutate: reSendInvitation, isLoading: isResendingInvitation } =
    useMutation(() => api.resendInvite(id, email));

  const menuOptions = useMemo(() => {
    const options = [
      { label: 'Edit user', value: 'edit', icon: <EditIcon /> },
      { label: 'Remove user', value: 'delete', icon: <DeleteIcon /> },
    ];

    if (!verified) {
      options.push({
        label: 'Resend invitation',
        value: 'resend',
        icon: <ResetIcon />,
      });
    }

    return options;
  }, [verified]);

  const handleMenuChange = (value: string) => {
    if (value === 'edit') {
      setUpdateUserId(id);
      setUpdateUserModalOpen(true);
    } else if (value === 'delete') {
      setDeletedUserId(id);
      setDeleteUserName(email);
      setDeleteUserModalOpen(true);
    } else if (value === 'resend') {
      reSendInvitation();
    }
  };

  return id !== user?.id ? (
    <MenuWrapper>
      <Menu
        menuButton={
          <IconButton height="20px">
            {isResendingInvitation ? <Loading size={18} /> : <MoreHorizIcon />}
          </IconButton>
        }
        options={menuOptions}
        onChange={handleMenuChange}
        closeOnChange
      />
    </MenuWrapper>
  ) : null;
};

export default UserMenu;
